import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";

export const LLMConfigSchema = z.object({
  operation: z.string(),
  model: z.string(),
  max_tokens: z.number().int().positive(),
  // Wire shape is `string | null` (backend serializes Decimal as a
  // string) — `.nullable()` short-circuits on `null` before the coercion
  // runs, so a genuinely unset temperature survives as `null` instead of
  // being coerced to `0`.
  temperature: z.coerce.number().nullable(),
  reasoning_effort: z.string().nullable(),
  // DUE-309: absent on an API response from before this column existed —
  // default to `null`, same "unset" meaning as an explicit null.
  verbosity: z.string().nullable().default(null),
  is_enabled: z.boolean(),
  notes: z.string().nullable(),
  updated_at: z.string(),
});
export type LLMConfig = z.infer<typeof LLMConfigSchema>;
const ListSchema = z.array(LLMConfigSchema);

// DUE-279: the catalogue the backend validates a PATCH against — which
// models exist, which efforts each one takes, what it costs. The admin
// page derives every option from it instead of mirroring the tables.
export const LLMModelSchema = z.object({
  name: z.string(),
  reasoning: z.boolean(),
  efforts: z.array(z.string()),
  default_effort: z.string().nullable(),
  price_in_per_1m: z.number(),
  price_out_per_1m: z.number(),
  // DUE-309: capability flag — does this model take a `verbosity` param.
  verbosity: z.boolean().default(false),
});
export type LLMModel = z.infer<typeof LLMModelSchema>;
const ModelListSchema = z.array(LLMModelSchema);

const KEY = ["admin", "llm-config"];
const MODELS_KEY = ["admin", "llm-models"];

export function useLLMConfig() {
  return useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) =>
      request({ path: "/api/admin/llm-config", schema: ListSchema, signal }),
  });
}

export function useLLMModels() {
  return useQuery({
    queryKey: MODELS_KEY,
    queryFn: ({ signal }) =>
      request({ path: "/api/admin/llm-models", schema: ModelListSchema, signal }),
    // The catalogue is a module constant on the backend: it only ever
    // changes with a deploy, so there is nothing to revalidate.
    staleTime: Infinity,
  });
}

export function usePatchLLMConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      operation: string;
      patch: Partial<{
        model: string;
        max_tokens: number;
        temperature: number;
        // DUE-279: an explicit `null` clears the column back to the
        // model family's default effort; an absent key leaves it as is.
        reasoning_effort: string | null;
        // DUE-309: same absent-vs-null split as `reasoning_effort`.
        verbosity: string | null;
        is_enabled: boolean;
        notes: string | null;
      }>;
    }) =>
      request({
        method: "PATCH",
        path: `/api/admin/llm-config/${args.operation}`,
        body: args.patch,
        schema: LLMConfigSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
